import { AgentTaskStatus, commandTypeFromJSON, CommandType } from '~/protos/agents'
import { TaskStatuses } from '~/constants/task-status'

export function normalizeCommandType(commandType: unknown): CommandType {
  if (typeof commandType === 'string') {
    const trimmed = commandType.trim()
    if (/^-?\d+$/.test(trimmed)) {
      return commandTypeFromJSON(Number(trimmed))
    }
    return commandTypeFromJSON(trimmed)
  }
  return commandTypeFromJSON(commandType)
}

/** Commands that generate documents/results instead of code diffs */
export const documentGeneratingCommands = [
  CommandType.INTEGRATION_TEST,
  CommandType.MUNIT_TEST_CREATION,
  CommandType.GENERATE_DOCUMENTATION,
  CommandType.GENERATE_MAPPING_TABLE,
  CommandType.GENERATE_DATAWEAVE,
  CommandType.DATAWEAVE_TO_DATA_CREATION,
  CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION,
  CommandType.GENERATE_DATAWEAVE_REPO_AWARE,
  CommandType.DATAWEAVE_TO_DATA_CREATION_REPO_AWARE,
  CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION_REPO_AWARE,
  CommandType.GENERATE_MULTI_REPO_FLOW_DIAGRAM,
  CommandType.MIGRATION_DOCUMENT,
  CommandType.MIGRATION_ACCELERATOR
]

/** Check if a command type generates documents instead of code diffs */
export function isDocumentGeneratingCommand(commandType: CommandType): boolean {
  return documentGeneratingCommands.includes(commandType)
}

/**
 * Single source of truth: returns the effective display status for a task.
 * Document-generating commands treat DIFF_GENERATED / DRAFT_GENERATED as
 * terminal (remapped to COMPLETED) since they don't require code review.
 */
export function getEffectiveTaskStatus(
  status: AgentTaskStatus,
  commandType: CommandType
): AgentTaskStatus {
  if (
    isDocumentGeneratingCommand(commandType) &&
    (status === AgentTaskStatus.DIFF_GENERATED || status === AgentTaskStatus.DRAFT_GENERATED)
  ) {
    return AgentTaskStatus.COMPLETED
  }
  return status
}

/**
 * Single source of truth: returns true when a task should be considered "done"
 * (no spinner, no action badge). Uses getEffectiveTaskStatus internally so the
 * document-command logic is never duplicated.
 */
export function isTaskEffectivelyDone(
  status: AgentTaskStatus | undefined,
  commandType: CommandType
): boolean {
  if (status === undefined) return false
  const effective = getEffectiveTaskStatus(status, commandType)
  const group = TaskStatuses[effective]?.group
  return group !== 'IN_PROGRESS' && group !== 'NEED_ACTION'
}

/**
 * Gets the document list title and description based on command type.
 */
export function getDocumentListProps(commandType: CommandType) {
  switch (commandType) {
    case CommandType.INTEGRATION_TEST:
      return { title: 'Results Generated', description: '' }
    case CommandType.GENERATE_DOCUMENTATION:
      return {
        title: 'Documentation Generated',
        description: 'Generated documentation files for the project'
      }
    case CommandType.GENERATE_MAPPING_TABLE:
      return {
        title: 'Mapping Tables Generated',
        description: 'Data mapping tables and transformation documents'
      }
    case CommandType.GENERATE_DATAWEAVE:
    case CommandType.DATAWEAVE_TO_DATA_CREATION:
    case CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION:
    case CommandType.GENERATE_DATAWEAVE_REPO_AWARE:
    case CommandType.DATAWEAVE_TO_DATA_CREATION_REPO_AWARE:
    case CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION_REPO_AWARE:
      return {
        title: 'DataWeave Generated',
        description: 'Generated DataWeave script and related artifacts'
      }
    case CommandType.GENERATE_MULTI_REPO_FLOW_DIAGRAM:
      return {
        title: 'Flow Diagrams Generated',
        description: 'Architecture and flow diagrams for the project'
      }
    case CommandType.MUNIT_TEST_CREATION:
      return {
        title: 'MUnit Tests Generated',
        description: 'Generated MUnit test files and test cases'
      }
    case CommandType.MIGRATION_DOCUMENT:
      return {
        title: 'Migration Documents Generated',
        description: 'Migration guides, plans, and documentation'
      }
    case CommandType.MIGRATION_ACCELERATOR:
      return {
        title: 'Migration Accelerator Output',
        description: 'Accelerated migration artifacts and documentation'
      }
    default:
      return { title: 'Generated Documents', description: 'Files generated for this task' }
  }
}
